import { Injectable } from '@angular/core';
import {Router} from "@angular/router";
import {OnBoardingDataInterface, UserInterface} from "../interfaces/user/user.interface";
import { user, getAdditionalUserInfo, getAuth, createUserWithEmailAndPassword, updateProfile, sendEmailVerification, GoogleAuthProvider, OAuthProvider, signInWithPopup, signInWithEmailAndPassword, signOut, sendPasswordResetEmail, reauthenticateWithCredential, updatePassword, useDeviceLanguage, EmailAuthProvider } from '@angular/fire/auth';
import {User, UserCredential} from "@firebase/auth";
import {Firestore, doc, updateDoc, setDoc, getDoc, onSnapshot} from "@angular/fire/firestore";
import {BehaviorSubject, firstValueFrom} from "rxjs";
import {environment} from "../../../environments/environment";
import {CommonModalsService} from "./modals/common-modals.service";
import {HttpClient} from "@angular/common/http";
import _ from 'lodash';
import {CannyService} from "./canny.service";
import {Unsubscribe} from "@firebase/firestore";

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  wrongCredentials = false;
  tooManyRequests = false;
  userNotFound = false;
  invalidEmail = false
  alreadyInUse = false
  unknownError = '';
  error = false
  public uid: string
  public email: string
  auth = getAuth()
  user$ = user(this.auth)
  admin = false
  onBoarded = true
  private userData = new BehaviorSubject<UserInterface | undefined>(undefined);
  userData$ = this.userData.asObservable();
  snapshotUnsub: Unsubscribe | undefined

  constructor(
    private router: Router,
    private firestore: Firestore,
    private commonModalSvc: CommonModalsService,
    private http: HttpClient,
    private cannySvc: CannyService
  ) {
    this.user$.subscribe((user) => {
      if (this.snapshotUnsub) {
        this.snapshotUnsub();
        this.snapshotUnsub = undefined;
      }

      this.uid = user?.uid ?? ''
      this.email = user?.email ?? ''
      console.log('UID', this.uid)
      if (this.uid) {
        this.snapshotUnsub = onSnapshot(doc(this.firestore, `users/${this.uid}`), async (userDoc) => {
          if (!userDoc.exists()) { return }
          const userData = userDoc.data() as UserInterface
          if (!userData.tour) {
            userData.tour = {
              workflow: false,
              workflowList: true,
              extraction: false
            }
            await this.updateUserTour('workflowList')
          }
          this.userData.next(userData);
          this.admin = _.get(userData, 'admin', false)
          this.onBoarded = _.get(userData, 'onBoarded', false)
        })
        console.log('User', user)
        this.cannySvc.identify({
          id: this.uid,
          email: this.email,
          name: user?.displayName ?? '',
          avatarURL: user?.photoURL ?? '',
        }).catch((err) => {})
      }
    })
  }

  async loginWithEmail(email: string, password: string): Promise<boolean> {
    return signInWithEmailAndPassword(this.auth, email, password)
      .then(async (credential) => {
        this.auth = getAuth()
        if (this.auth.currentUser?.emailVerified) {
          await this.router.navigate(['/'])
        } else {
          await this.router.navigate(['/auth/validate-email'])
        }
        return Promise.resolve(true);
      });
  }

  loginWithSocial(providerId: 'google.com' | 'microsoft.com') {
    const provider = providerId === 'google.com' ? new GoogleAuthProvider() : new OAuthProvider(providerId);
    provider.setCustomParameters({
      prompt: 'select_account'
    })
    useDeviceLanguage(this.auth)
    signInWithPopup(this.auth, provider)
      .then(async (result) => {
        this.auth = getAuth()
        const additionalInfo = getAdditionalUserInfo(result)
        if (additionalInfo?.isNewUser) {
          await this.createInitialFireStructure(result)
          await this.router.navigate(['/on-boarding'])
        } else {
          const userData = await this.getUserData(this.auth.currentUser?.uid)
          if (!userData) {
            console.log('No user. Wtf?')
            await this.router.navigate(['/auth/login']);
          } else {
            if (userData.onBoarded) {
              console.log('Already onboarded')
              await this.router.navigate(['/']);
            } else {
              console.log('user not onboarded')
              await this.router.navigate(['/on-boarding']);
            }
          }
        }
      })
      .catch((err) => {
        console.error('Login error', err);
        if (err?.code === 'auth/popup-closed-by-user' || err?.code === 'auth/cancelled-popup-request') { return }
        this.errorSelector(err)
      })
  }

  logout() {
    signOut(this.auth)
      .then(async () => {
        localStorage.removeItem('lastWorkspaceUsed')
        localStorage.removeItem('workspaceId')
        this.userData.next(undefined)
        await this.router.navigate(['/auth', 'login']);
      })
      .catch(err => {
        console.error('Log out error: ', err);
      });
  }

  async sendVerificationEmail() {
    useDeviceLanguage(this.auth)
    if (this.auth.currentUser) {
      await sendEmailVerification(this.auth.currentUser)
    }
  }

  async sendResetPasswordEmail(email: string) {
    useDeviceLanguage(this.auth)
    try {
      await sendPasswordResetEmail(this.auth, email)
      return true
    } catch (e) {
      return false
    }
  }

  async updateUserPassword(newPassword: string) {
    await updatePassword(this.auth.currentUser as User, newPassword)
  }

  async updateDisplayName(newName: string) {
    const user = this.auth.currentUser as User
    await updateProfile(user, {
      displayName: newName
    })
    await updateDoc(doc(this.firestore, `users/${user.uid}`), {
      name: newName
    })
  }

  public reauthenticateUserWithPassword(email: string, password: string){
    const user = this.auth.currentUser as User
    const credential = EmailAuthProvider.credential(email, password);
    return reauthenticateWithCredential(user, credential)
  }

  async signUpByEmail(name: string, email: string, password: string) {
    return createUserWithEmailAndPassword(this.auth, email, password)
      .then(async (userCredential) => {
        this.auth = getAuth()
        useDeviceLanguage(this.auth)
        if (this.auth.currentUser) {
          await updateProfile(this.auth.currentUser, {
            displayName: name
          })
        }
        await this.createInitialFireStructure(userCredential)
        await this.sendVerificationEmail()
        await this.router.navigate(['/auth/validate-email'])
        return Promise.resolve(true)
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  }

  async createInitialFireStructure(user: UserCredential) {
    const data: UserInterface = {
      uid: user.user?.uid ?? '',
      name: user.user?.displayName ?? '',
      email: user.user?.email ?? '',
      admin: false,
      onBoarded: false,
      tour: {
        workflow: false,
        workflowList: false,
        extraction: false
      }
    }
    const d = doc(this.firestore, `users/${data.uid}`)
    await setDoc(d, data)
  }

  async updateUserOnBoarding(data: OnBoardingDataInterface, uid: string) {
    const d = doc(this.firestore, `users/${uid}`)
    await updateDoc(d, {
      onBoardingData: data,
      onBoarded: true
    })
  }

  async updateUserTour(section: string) {
    const d = doc(this.firestore, `users/${this.uid}`)
    await updateDoc(d, {
      [`tour.${section}`]: true
    })
  }

  errorSelector(error: {code: string, message: string}) {
    this.error = true
    switch (error.code) {
      case 'auth/invalid-email':
        this.invalidEmail = true
        break
      case 'auth/wrong-password':
        this.wrongCredentials = true
        break
      case 'auth/user-not-found':
        this.userNotFound = true
        break
      case 'auth/too-many-requests':
        this.tooManyRequests = true;
        break
      case 'auth/email-already-in-use':
        this.alreadyInUse = true;
        break
      default:
        this.unknownError = error.message
    }
  }

  resetErrors() {
    this.error = false
    this.userNotFound = false
    this.wrongCredentials = false;
    this.invalidEmail = false
    this.unknownError = '';
  }

  async getIdToken(): Promise<string | null> {
    return (await this.auth.currentUser?.getIdToken()) ?? null
  }

  /**
   * Like getIdToken, but waits for Firebase to restore any persisted session first.
   *
   * `currentUser` is null until that restore completes, so on a route that renders immediately —
   * the public booking page — a plain getIdToken() reports a signed-in user as anonymous roughly
   * every time. Only use this where the caller genuinely might be signed out.
   */
  async getIdTokenWhenReady(): Promise<string | null> {
    try {
      await this.auth.authStateReady()
      return (await this.auth.currentUser?.getIdToken()) ?? null
    } catch {
      return null
    }
  }

  async getUserData(uid?: string) {
    uid = uid ?? this.uid
    const d = doc(this.firestore, `users/${uid}`)
    const docSnap = await getDoc(d)
    if (docSnap.exists()) {
      return docSnap.data() as UserInterface
    }
    return null
  }

  async getAuthToken(vendor: string, workspaceId: string, integrationId: string, code: string, isNew = true) {
    const data = {
      code,
      mode: isNew ? 'new' : 'refresh',
      workspaceId,
      integrationId,
      fromLocalhost: location.hostname === 'localhost'
    }
    try {
      return await firstValueFrom(this.http.post(environment.fireServer + `/integrations/${vendor}/get-access-token`, data)) as {
        accessToken: string
        expiresIn: number
        refreshToken: string
        tokenType: string
      }
    } catch (e: any) {
      await this.commonModalSvc.showErrorModal(e.message, e.status, 'could-not-get-valid-access-token', `refresh ${vendor} access token`, data)
      return null
    }
  }
}
